import type { JSONContent } from "@tiptap/core"
import { attrSectionByKey } from "@/lib/entity-profile-section-defs"
import { mentionKey, mentionToken, type MentionCategory, type MentionItem } from "./types"

const ENTITY_TOKEN_RE = /@(hero|loc|cam)\[(\d+)\]|@(?:reference|markup)_(\d+)/g
const PROP_TOKEN_RE = /@prop\[(\d+)(?::(\d+))?\]/g
const ATTR_TOKEN_RE = /@attr\[([^\]]+)\]/g
const NODE_TOKEN_RE = /@node\[(\d+)\]/g
/** Video-correction markup chips: @HH:MM:SS:FF */
const TIMECODE_MENTION_RE = /@(\d{2}:\d{2}:\d{2}:\d{2})/g
const BLOCK_HEADER_RE = /^\[(\d+\s*-\s*\d+\s*sec|\d{1,2}:\d{2})\]\s*$/
const BLOCK_CAMERA_RE = /^@cam\[(\d+)\]\s*$/
/** Быстрая проверка: в тексте есть токены промпта, которые нужно восстановить как чипы при paste. */
const PROMPT_TOKEN_TEST_RE =
  /@(?:hero|loc|cam|prop|attr|node)\[[^\]]*\]|@(?:reference|markup)_\d+|@\d{2}:\d{2}:\d{2}:\d{2}/

export const DEFAULT_TIMECODE = "0-1 sec"

export function textContainsPromptTokens(text: string): boolean {
  return PROMPT_TOKEN_TEST_RE.test(text ?? "")
}

const CAM_TOKEN_RE = /@cam\[(\d+)\]/g

/** Уникальные id камер из текста промпта (порядок первого вхождения). */
export function camIdsFromPromptText(text: string): number[] {
  const ids: number[] = []
  const seen = new Set<number>()
  CAM_TOKEN_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = CAM_TOKEN_RE.exec(text ?? "")) !== null) {
    const id = Number(m[1])
    if (!Number.isFinite(id) || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

type ResolveLabel = (category: MentionCategory, id: number, stateId?: number | null) => string

/** Подпись для токена при разборе текста: имя из источников или запасной вариант. */
function buildResolver(items: MentionItem[]): ResolveLabel {
  const byKey = new Map<string, string>()
  for (const it of items) {
    byKey.set(mentionKey(it.category, it.id, it.stateId, it.attrKey), it.label)
  }
  return (category, id, stateId) => {
    const fromItems = byKey.get(mentionKey(category, id, stateId))
    if (fromItems) return fromItems
    if (category === "reference") return `@reference_${id}`
    return mentionToken(category, id, `@${category}[${id}]`, stateId)
  }
}

function resolveAttr(items: MentionItem[], attrKey: string): { id: number; label: string } {
  const fromItems = items.find((it) => it.category === "attr" && it.attrKey === attrKey)
  if (fromItems) return { id: fromItems.id, label: fromItems.label }
  const def = attrSectionByKey(attrKey)
  return { id: def?.mentionId ?? 0, label: def?.label ?? attrKey }
}

function parseInline(line: string, resolve: ResolveLabel, items: MentionItem[]): JSONContent {
  const nodes: JSONContent[] = []
  let lastIndex = 0

  const pushText = (offset: number) => {
    if (offset > lastIndex) {
      nodes.push({ type: "text", text: line.slice(lastIndex, offset) })
    }
  }

  const pushMention = (
    match: string,
    category: MentionCategory,
    id: number,
    stateId: number | null | undefined,
    offset: number,
    attrKey?: string | null,
    labelOverride?: string | null,
  ) => {
    pushText(offset)
    const attr = category === "attr" && attrKey ? resolveAttr(items, attrKey) : null
    nodes.push({
      type: "mention",
      attrs: {
        category,
        id: attr?.id ?? id,
        label: attr?.label ?? labelOverride ?? resolve(category, id, stateId),
        ...(category === "prop" && stateId != null ? { stateId } : {}),
        ...(category === "attr" && attrKey ? { attrKey } : {}),
      },
    })
    lastIndex = offset + match.length
  }

  type Hit = {
    kind: "mention"
    index: number
    match: string
    category: MentionCategory
    id: number
    stateId: number | null
    attrKey?: string
    label?: string
  }

  const hits: Hit[] = []
  const combinedRe = new RegExp(
    `${ENTITY_TOKEN_RE.source}|${PROP_TOKEN_RE.source}|${ATTR_TOKEN_RE.source}|${NODE_TOKEN_RE.source}`,
    "g",
  )
  let m: RegExpExecArray | null
  while ((m = combinedRe.exec(line)) !== null) {
    if (m[1] != null || m[3] != null) {
      hits.push({
        kind: "mention",
        index: m.index,
        match: m[0],
        category: (m[1] ?? "reference") as MentionCategory,
        id: Number(m[2] ?? m[3] ?? 0),
        stateId: null,
      })
    } else if (m[4] != null) {
      const stateRaw = m[5]
      hits.push({
        kind: "mention",
        index: m.index,
        match: m[0],
        category: "prop",
        id: Number(m[4]),
        stateId: stateRaw != null && stateRaw !== "" ? Number(stateRaw) : null,
      })
    } else if (m[6] != null) {
      const attrKey = m[6]
      const resolved = resolveAttr(items, attrKey)
      hits.push({
        kind: "mention",
        index: m.index,
        match: m[0],
        category: "attr",
        id: resolved.id,
        stateId: null,
        attrKey,
      })
    } else if (m[7] != null) {
      hits.push({
        kind: "mention",
        index: m.index,
        match: m[0],
        category: "node",
        id: Number(m[7]),
        stateId: null,
      })
    }
  }
  TIMECODE_MENTION_RE.lastIndex = 0
  while ((m = TIMECODE_MENTION_RE.exec(line)) !== null) {
    const label = m[0]
    const fromItems = items.find((it) => it.category === "reference" && it.label === label)
    hits.push({
      kind: "mention",
      index: m.index,
      match: label,
      category: "reference",
      id: fromItems?.id ?? 0,
      stateId: null,
      label,
    })
  }
  hits.sort((a, b) => a.index - b.index)

  for (const hit of hits) {
    if (hit.index < lastIndex) continue
    pushMention(hit.match, hit.category, hit.id, hit.stateId, hit.index, hit.attrKey, hit.label)
  }

  if (lastIndex < line.length) {
    nodes.push({ type: "text", text: line.slice(lastIndex) })
  }
  return nodes.length > 0 ? { type: "paragraph", content: nodes } : { type: "paragraph" }
}

function isEmptyParagraph(node: JSONContent | undefined): boolean {
  return node?.type === "paragraph" && (!node.content || node.content.length === 0)
}

/** Убрать хвостовые пустые paragraph (лишний \\n из clipboard). */
export function trimTrailingEmptyParagraphs(content: JSONContent[]): JSONContent[] {
  const out = [...content]
  while (out.length > 1 && isEmptyParagraph(out[out.length - 1])) {
    out.pop()
  }
  return out
}

/** Текст с токенами (из БД) -> Tiptap-документ с чипами и блоками. */
export function textToDoc(text: string, items: MentionItem[]): JSONContent {
  const resolve = buildResolver(items)
  const lines = (text ?? "").replace(/\r\n/g, "\n").split("\n")
  const content: JSONContent[] = []
  let currentBlock: JSONContent | null = null

  for (const line of lines) {
    const header = line.match(BLOCK_HEADER_RE)
    if (header) {
      currentBlock = { type: "promptBlock", attrs: { timecode: header[1], cameraPresetId: null }, content: [] }
      content.push(currentBlock)
      continue
    }
    if (currentBlock && (currentBlock.content as JSONContent[]).length === 0) {
      const cameraMatch = line.match(BLOCK_CAMERA_RE)
      if (cameraMatch) {
        const n = Number(cameraMatch[1])
        currentBlock.attrs = {
          ...(currentBlock.attrs ?? {}),
          cameraPresetId: Number.isFinite(n) ? n : null,
        }
        continue
      }
    }
    const para = parseInline(line, resolve, items)
    if (currentBlock) {
      ;(currentBlock.content as JSONContent[]).push(para)
    } else {
      content.push(para)
    }
  }

  for (const node of content) {
    if (node.type === "promptBlock" && (!node.content || (node.content as JSONContent[]).length === 0)) {
      node.content = [{ type: "paragraph" }]
    }
  }

  return { type: "doc", content: content.length > 0 ? content : [{ type: "paragraph" }] }
}

function inlineToText(node: JSONContent): string {
  if (!node.content) return ""
  let out = ""
  for (const child of node.content) {
    if (child.type === "text") {
      out += child.text ?? ""
    } else if (child.type === "mention") {
      const category = (child.attrs?.category ?? "reference") as MentionCategory
      const id = Number(child.attrs?.id ?? 0)
      const label = String(child.attrs?.label ?? "")
      const stateIdRaw = child.attrs?.stateId
      const stateId = stateIdRaw == null || stateIdRaw === "" ? null : Number(stateIdRaw)
      const attrKey = child.attrs?.attrKey == null || child.attrs?.attrKey === "" ? null : String(child.attrs.attrKey)
      out += mentionToken(category, id, label, stateId, attrKey)
    }
  }
  return out
}

/** Tiptap-документ -> текст с токенами для бэкенда. */
export function docToText(doc: JSONContent): string {
  const lines: string[] = []
  const nodes = doc.content ?? []
  for (const node of nodes) {
    if (node.type === "promptBlock") {
      const tc = String(node.attrs?.timecode ?? DEFAULT_TIMECODE)
      lines.push(`[${tc}]`)
      const cameraPresetIdRaw = node.attrs?.cameraPresetId
      if (cameraPresetIdRaw != null && cameraPresetIdRaw !== "") {
        const n = Number(cameraPresetIdRaw)
        if (Number.isFinite(n)) lines.push(`@cam[${n}]`)
      }
      for (const child of node.content ?? []) {
        if (child.type === "paragraph") lines.push(inlineToText(child))
      }
    } else if (node.type === "paragraph") {
      lines.push(inlineToText(node))
    }
  }
  return lines.join("\n")
}
